/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';

/**
 * @prop {String} label - Defines the label shown below the number.
 * @prop {String} info - Defines additional guidance text shown below the label.
 * @prop {String} number - Defines number of the stepper item.
 * @prop {String} type - Defines the type of the stepper item and the badge shown in it. Possible values are `success`, `warning` and `error`. If let undefined no badge will be shown.
 * @prop {Boolean} disabled - Default false. Defines if the stepper item is disabled or not.
 * @prop {Boolean} active - Default false. Defines if the stepper item is active or not. If set to `true`, the disabled property will automatically be set to false.
 * @prop {Boolean} condensed - Default false. Adds condensed styles if set to `true`.
 * @prop {String} color - Default neutral. Adds the primary or neutral styles as per the value 'primary' or 'neutral'.
 */
export class SHStepperItem extends ControlStateMixin(FocusBlurMixin(LitElement)) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      info: { type: String, reflect: true },
      number: { type: String, reflect: true },
      type: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      color: { type: String, reflect: true },
    };
  }
  static get styles() {
    return [
      css`
        :host {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          position: relative;
          outline: 0;
        }
        sh-text {
          text-align: center;
          padding: 0 0.5rem;
          box-sizing: border-box;
          color: var(--text-secondary);
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .circle {
          height: 2rem;
          width: 2rem;
          margin-bottom: 0.5rem;
          border-radius: 50%;
          text-align: center;
          font: var(--header-2);
          color: var(--text-secondary);
          line-height: 2rem;
          transition: var(--transition-time) all ease-in-out;
          background: rgba(var(--ui-1), var(--opacity-6));
          border: 0.0625rem solid transparent;
          position: relative;
          box-sizing: border-box;
        }
        :host(:hover) .circle,
        :host(.hover) .circle,
        :host(:hover) .label {
          color: var(--text-primary);
        }
        :host(:hover) .info {
          color: var(--text-secondary);
        }
        .line-left,
        .line-right {
          position: absolute;
          top: 0.9375rem;
          width: calc(50% - 1.5rem);
          border-bottom: 0.125rem solid rgba(var(--ui-1), var(--opacity-6));
        }
        :host([condensed]) .line-left,
        :host([condensed]) .line-right {
          top: 0.4375rem;
          width: calc(50% - 0.5625rem);
        }
        .line-left {
          left: 0rem;
        }
        .line-right {
          right: 0rem;
        }
        :host(:first-of-type) .line-left {
          display: none;
        }
        :host(:last-of-type) .line-right {
          display: none;
        }
        :host([active]) .circle {
          color: rgba(var(--ui-7), var(--opacity-1));
          background: rgba(var(--ui-1), var(--opacity-3));
        }
        :host([color='primary'][active]) .circle {
          color: var(--text-white);
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        sh-text[size='title-1'] {
          line-height: 1.5rem;
        }
        :host([active]) sh-text[size='title-1'] {
          color: var(--text-primary);
        }
        :host([half]) .circle {
          background: linear-gradient(
            90deg,
            rgba(var(--ui-1), var(--opacity-5)) 50%,
            transparent 50%
          );
          background-size: calc(100% + 0.25rem);
          background-position: -0.125rem;
          border-color: var(--text-disabled);
        }
        :host([disabled]) {
          cursor: default;
          pointer-events: none;
        }
        :host([disabled]) .circle {
          color: var(--text-disabled);
          background: transparent;
          border-color: var(--text-disabled);
        }
        :host([disabled]) sh-text {
          color: var(--text-disabled);
        }
        :host([condensed]) .circle {
          border-width: 0.125rem;
          color: transparent;
          height: 0.875rem;
          width: 0.875rem;
          margin: 0.0625rem 0.125rem;
        }
        :host([condensed][active]:not([disabled])) .circle {
          background: rgba(var(--ui-1), var(--opacity-3));
        }
        :host([condensed]:not([disabled])) .circle {
          background: rgba(var(--ui-1), var(--opacity-5));
        }
        :host([color='primary'][condensed][active]:not([disabled])) .circle {
          background: rgba(var(--ui-3), var(--opacity-1));
        }
        :host([condensed]) .line-left,
        :host([condensed]) .line-right {
          width: calc(50% - 0.625rem);
          border-bottom: 0.125rem solid rgba(var(--ui-1), var(--opacity-6));
        }
        sh-badge {
          position: absolute;
          top: -0.25rem;
          right: -0.25rem;
        }
        :host #stepperCircle.item-focused {
          border: var(--focus-outline);
        }
        :host([condensed]) sh-badge {
          top: -0.1875rem;
          right: -0.1875rem;
        }
        :host([type][condensed]) .circle {
          background: transparent;
          border-color: transparent;
        }
      `,
    ];
  }
  render() {
    return html`
      <div class="line-left"></div>
      <div class="line-right"></div>
      <div class="circle focus-item" id="stepperCircle">
        ${this.number && !this.condensed ? html` <div class="number">${this.number}</div>` : ''}
        ${this.type ? html` <sh-badge .type="${this.type}"></sh-badge>` : ''}
      </div>
      ${this.label && !this.condensed
        ? html`<sh-text class="label" size="title-1">${this.label}</sh-text>`
        : ''}
      ${this.info ? html`<sh-text class="info" size="body-2">${this.info}</sh-text>` : ''}
    `;
  }

  firstUpdated() {
    super.firstUpdated();
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('active')) {
      this._handleActiveChange();
    }
    const listOfProperties = ['label', 'info', 'number', 'type', 'active', 'condensed', 'color'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  constructor() {
    super();
    this.color = 'neutral';
  }

  _spaceKeyAction() {
    super._spaceKeyAction();
    this.click();
  }

  _handleActiveChange() {
    if (this.disabled) {
      this.disabled = false;
    }
  }
}

customElements.define('sh-stepper-item', SHStepperItem);
